import { sessionError } from './sessionTransition'

const mutations = ['/login', '/signin', '/logout', '/redirect-session']
const publicRequests = ['get /version', 'post /forgot-password', 'post /reset-password', 'post /exist-token']

// A separate opt-in facade captures intent synchronously (Axios 0.21 request
// interceptors execute later in a Promise). No global/default headers or hooks.
export function createSessionClient(axios, defaults, session, pageURL) {
  const client = axios.create(defaults)
  const base = new URL(defaults.baseURL, pageURL)
  const adapter = client.defaults.adapter
  function request(config) {
    let ticket
    let cancelled = false
    let release = () => {}
    let cleanupSignal = () => {}
    let transportConfig
    let method
    let path
    const source = axios.CancelToken.source()
    const cancel = () => { cancelled = true; source.cancel('SESSION_CANCELLED') }
    const scrub = value => {
      if (!value) return
      value.data = null
      if (value.headers) Object.keys(value.headers).forEach(key => {
        if (key.toLowerCase() === 'x-promo-session') delete value.headers[key]
      })
    }
    let safeConfig
    try {
      method = (config.method || 'get').toLowerCase()
      // Axios combines leading slash paths with baseURL; absolute URLs override it.
      const raw = config.url || ''
      if (typeof raw !== 'string' || /[\\\u0000-\u0020]/.test(raw)) throw sessionError('SESSION_DESTINATION_DENIED')
      const absolute = /^([a-z][a-z\d+.-]*:)?\/\//i.test(raw)
      const target = new URL(absolute ? raw : `${base.href.replace(/\/+$/, '')}/${raw.replace(/^\/+/, '')}`, pageURL)
      const prefix = base.pathname.replace(/\/+$/, '')
      if (!['https:', 'http:'].includes(base.protocol) || target.origin !== base.origin || target.username || target.password ||
          (prefix && !target.pathname.startsWith(`${prefix}/`))) throw sessionError('SESSION_DESTINATION_DENIED')
      path = target.pathname.slice(prefix.length)
      const mutation = method === 'post' && mutations.includes(path)
      if (mutations.includes(path) && !mutation) throw sessionError('SESSION_MUTATION_REQUIRED')
      if (mutation && !config.attempt) throw sessionError('SESSION_MUTATION_REQUIRED')
      if (mutation || !publicRequests.includes(`${method} ${path}`)) ticket = session.request(mutation ? config.attempt : null)
      if (config.signal) {
        if (config.signal.aborted) cancel()
        config.signal.addEventListener('abort', cancel)
        cleanupSignal = () => config.signal.removeEventListener('abort', cancel)
      }
      if (config.cancelToken) {
        config.cancelToken.throwIfRequested()
        config.cancelToken.promise.then(cancel)
      }
      if (ticket) release = ticket.cancelWith(cancel)
      // Whitelist options: callers cannot override destination, adapter, transforms,
      // credentials, headers or the bounded timeout. No marker in Axios defaults.
      safeConfig = {
        url: target.href, method, data: config.data, params: config.params,
        timeout: 60000, withCredentials: true, cancelToken: source.token,
        headers: { 'Content-Type': 'application/json' },
        adapter: current => {
          transportConfig = current
          if (cancelled || (ticket && !ticket.current())) throw sessionError('SESSION_OBSOLETE')
          const binding = ticket && ticket.binding()
          if (binding) current.headers['X-Promo-Session'] = binding
          return adapter(current)
        },
      }
    } catch (error) {
      config.data = null
      cleanupSignal()
      release()
      return Promise.reject(sessionError(error && error.code || 'SESSION_CANCELLED'))
    }
    return client.request(safeConfig).then(response => {
      if (cancelled || (ticket && !ticket.current())) throw sessionError('SESSION_OBSOLETE')
      // Never expose Axios config/request (cipher, password, headers) to consumers.
      return { data: response.data, status: response.status, headers: response.headers }
    }, error => {
      const obsolete = cancelled || (ticket && !ticket.current())
      const status = error && error.response && error.response.status
      const body = error && error.response && error.response.data
      const disabledRedirect = !obsolete && method === 'post' && path === '/redirect-session' && status === 503 &&
        body && typeof body === 'object' && !Array.isArray(body) && Object.keys(body).length === 2 &&
        Object.prototype.hasOwnProperty.call(body, 'code') && Object.prototype.hasOwnProperty.call(body, 'message') &&
        body.code === 'REDIRECT_AUTH_DISABLED' && typeof body.message === 'string'
      throw sessionError(obsolete ? 'SESSION_OBSOLETE' : disabledRedirect ? 'REDIRECT_AUTH_DISABLED' : 'SESSION_REQUEST_FAILED', status)
    }).finally(() => {
      release()
      cleanupSignal()
      scrub(transportConfig)
      scrub(safeConfig)
      config.data = null
      ticket = null
    })
  }
  const facade = { request }
  ;['get', 'delete', 'head', 'options'].forEach(method => {
    facade[method] = (url, config = {}) => request({ ...config, method, url })
  })
  ;['post', 'put', 'patch'].forEach(method => {
    facade[method] = (url, data, config = {}) => request({ ...config, method, url, data })
  })
  return facade
}
